/* eslint-disable no-nested-ternary */
/* eslint-disable arrow-body-style */
import React, { createContext, useState, useRef, useCallback, useMemo, useEffect } from 'react';
import * as yup from 'yup';
import { useFormik } from 'formik';
import moment from 'moment';
import useAuth from 'hooks/useAuth';
import usePermission from 'hooks/usePermission';
import {
    useGetSessionStudentsQuery,
    useChangeStudentAttendanceMutation,
    useUpdateStudentAttendanceMutation
} from 'store/services/school/session';
import { CLASS } from 'constant';
import useSnackbar from 'hooks/useSnackbar';
import PropTypes from 'prop-types';
import { useLocation } from 'react-router-dom';

const validationSchema = yup.object({
    att_student: yup.string().optional(),
    att_classroom: yup.object().optional().nullable(),
    att_date: yup
        .date()
        .required('Date is required')
        .typeError('Please enter a valid Date')
        .nullable()
        .test('date', 'Date should be less than or equal to today', (value) => moment(value).isSameOrBefore(new Date())),

    att_location: yup.object().required('Please select a location').nullable(),
    ch_student: yup.string().optional(),
    ch_classroom: yup.object().optional().nullable(),
    ch_date: yup
        .date()
        .required('Date is required')
        .typeError('Please enter a valid Date')
        .nullable()
        .test('date', 'Date should be less than or equal to today', (value) => moment(value).isSameOrBefore(new Date()))
    // ch_location: yup.object().required('Please select a location').nullable()
});
// Create context
const AttendanceContext = createContext();

// Create a provider component
export const AttendanceProvider = ({ children }) => {
    const location = useLocation();
    const path = location.pathname; // e.g. "/eidt/itp" or "/addt/itp"

    const isEIDT = path.toLowerCase().includes('/eidt/') && 'eidt';
    const isADDT = path.toLowerCase().includes('/addt/') && 'addt';
    let classtype = isEIDT || isADDT;
    console.log('class::', classtype);

    const { isServiceAvailable } = usePermission();
    // let classtype;
    // let classtype;
    if (!isEIDT && !isADDT && isServiceAvailable('EIDT') && isServiceAvailable('ADDT')) {
        classtype = null;
    } else if (!isEIDT && !isADDT && isServiceAvailable('EIDT')) {
        classtype = 'eidt';
    } else if (!isEIDT && !isADDT && isServiceAvailable('ADDT')) {
        classtype = 'addt';
    }

    const service = classtype ? CLASS[classtype] : null;

    const classRef = useRef();
    const { user } = useAuth();
    const { notify } = useSnackbar();
    const { hasPermission } = usePermission();

    // Memoize location ID to prevent unnecessary re-renders
    const locationId = useMemo(() => user?.locations?.[0]?.id, [user?.locations]);

    const [page, setPage] = useState(1);
    const [changeStudentAttendance] = useChangeStudentAttendanceMutation();
    const [updateStudentAttendance, { isLoading: loadingUpdateAttendance }] = useUpdateStudentAttendanceMutation();

    // Simplified initial filter state
    const initialFilterState = {
        search: '',
        classroom: '',
        location: null,
        status: null,
        date: new Date()
    };

    const [filters, setFilters] = useState({
        attendance: { ...initialFilterState },
        pickup: { ...initialFilterState }
    });
    const [showAttendance, setShowAttendance] = useState(true);
    const [showPickup, setShowPickup] = useState(true);
    // Create reusable payload generator
    const createPayload = useCallback(
        (filterType) => {
            const serviceId =
                filters[filterType].service?.location_type?.location_type === 'EIDT'
                    ? 5
                    : filters[filterType].service?.location_type?.location_type === 'ADDT'
                      ? 6
                      : null;
            const payload = {
                filters: {
                    client_id: user?.client_id,
                    location_id: filters[filterType]?.location,
                    service: service || serviceId,
                    start_date: moment(new Date(filters[filterType].date)).startOf('day').utc().format('YYYY-MM-DD HH:mm'),
                    end_date: moment(new Date(filters[filterType].date)).endOf('day').utc().format('YYYY-MM-DD HH:mm'),
                    ...(filters[filterType].search && { student_name: filters[filterType].search }),
                    ...(filters[filterType].classroom && { class_room_id: filters[filterType].classroom }),
                    status: filters[filterType]?.status?.id
                },
                pagination: {
                    current_page: parseInt(page, 10)
                }
            };

            return payload;
        },
        [filters, page, locationId, user?.client_id]
    );

    // Use memoized payloads
    const attendancePayload = useMemo(() => createPayload('attendance'), [createPayload]);
    const pickUpPayload = useMemo(() => createPayload('pickup'), [createPayload]);

    console.log('attendancePayload::', attendancePayload?.filters?.location_id);
    const [attendanceData, setAttendanceData] = useState(null);
    const [pickUpData, setPickUpData] = useState(null);

    const {
        data: attendanceResp,
        isLoading: attendanceLoading,
        isFetching: attendanceFetching,
        refetch: refetchAttendance
    } = useGetSessionStudentsQuery(attendancePayload, {
        skip: attendancePayload?.filters?.location_id === null
    });

    const {
        data: pickUpResp,
        isLoading: pickUpLoading,
        isFetching: pickUpFetching,
        refetch: refetchPickup
    } = useGetSessionStudentsQuery(pickUpPayload, {
        skip: pickUpPayload?.filters?.location_id === null
    });

    useEffect(() => {
        if (attendanceResp) {
            setAttendanceData(attendanceResp);
        }
    }, [attendanceResp]);

    useEffect(() => {
        if (pickUpResp) {
            setPickUpData(pickUpResp);
        }
    }, [pickUpResp]);

    const formik = useFormik({
        initialValues: {
            parent: null,
            att_student: '',
            att_location: null,
            att_classroom: null,
            att_date: new Date(),
            att_status: null,
            ch_student: '',
            ch_location: null,
            ch_classroom: null,
            ch_date: new Date(),
            ch_status: null
        },
        validationSchema,
        onSubmit: (values) => {
            setShowAttendance(true);
            setPage(1);
            if (values.parent === 'attendance') {
                setFilters((prev) => ({
                    ...prev,
                    attendance: {
                        search: values.att_student,
                        classroom: values.att_classroom?.cls_id,
                        date: values.att_date,
                        location: values.att_location?.id,
                        status: values.att_status,
                        service: values.att_location
                    }
                }));
                // Force refetch after state update and update data directly
                setTimeout(async () => {
                    if (values.att_location?.id) {
                        const result = await refetchAttendance();
                        if (result?.data) {
                            setAttendanceData(result.data);
                        }
                    }
                }, 0);
            } else {
                // setShowPickup(true);
                setFilters((prev) => ({
                    ...prev,
                    pickup: {
                        search: values.ch_student,
                        classroom: values.ch_classroom?.cls_id,
                        date: values.ch_date,
                        location: values.ch_location?.id,
                        status: values.ch_status
                    }
                }));
                // Force refetch after state update and update data directly
                setTimeout(async () => {
                    if (values.ch_location?.id) {
                        const result = await refetchPickup();
                        if (result?.data) {
                            setPickUpData(result.data);
                        }
                    }
                }, 0);
            }
        }
    });

    console.log('formik::', formik);
    const handleReset = useCallback(
        (type) => {
            setFilters((prev) => ({
                ...prev,
                [type]: { ...initialFilterState }
            }));
            // Reset corresponding Formik fields based on type
            if (type === 'attendance') {
                // alert()
                formik.setFieldValue('att_student', '');
                formik.setFieldValue('att_classroom', null);
                formik.setFieldValue('att_location', null);
                formik.setFieldValue('att_status', null);
                formik.setFieldValue('att_date', new Date());
                setPage(1);
            } else if (type === 'pickup') {
                formik.setFieldValue('ch_student', '');
                formik.setFieldValue('ch_classroom', '');
                formik.setFieldValue('ch_date', new Date());
                formik.setFieldValue('ch_location', null);
                formik.setFieldValue('ch_status', null);
                setPage(1);
            }
            setAttendanceData(null);
            setPickUpData(null);
            // eslint-disable-next-line react-hooks/exhaustive-deps
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [formik]
    );

    useEffect(() => {
        handleReset('attendance');
    }, [path]);
    
    const handlePresent = useCallback(
        async (id, location, present, parent) => {
            try {
                const filterType = parent === 'attendance' ? 'attendance' : 'pickup';
                const payload = {
                    filters: {
                        student_id: id,
                        location_id: location,
                        start_date: moment(filters[filterType].date).startOf('day').utc().format('YYYY-MM-DD HH:mm'),
                        end_date: moment(filters[filterType].date).endOf('day').utc().format('YYYY-MM-DD HH:mm'),
                        present_date: moment(filters[filterType].date).utc().format('YYYY-MM-DD HH:mm'),
                        service
                    }
                };

                const res = await changeStudentAttendance({ payload, present }).unwrap();
                notify({ message: res.message?.[0] || 'Success', severity: 'success' });
                return res;
            } catch (error) {
                const errorMessage = error.data?.message ? error.data.message[0] : 'Something went wrong';
                notify({ message: errorMessage, severity: 'error' });
                return error;
                // throw error;
            }
            // eslint-disable-next-line react-hooks/exhaustive-deps
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [filters, changeStudentAttendance, notify, service]
    );

    const handleUpdateAttendance = useCallback(
        async (id, location, parent, attendanceId, data, handleClose) => {
            try {
                const payload = {
                    filters: {
                        attendance_id: attendanceId,
                        ...data
                    }
                };

                const res = await updateStudentAttendance({ payload }).unwrap();
                notify({ message: res.message?.[0] || 'Success', severity: 'success' });
                handleClose();
                return res;
            } catch (error) {
                const errorMessage = error.data?.message ? error.data.message[0] : 'Something went wrong';
                notify({ message: errorMessage, severity: 'error' });
                handleClose();
                return error;
                // throw error;
            }
            // eslint-disable-next-line react-hooks/exhaustive-deps
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [filters, updateStudentAttendance, notify, service]
    );

    // Memoize context value
    const contextValue = useMemo(
        () => ({
            payloadAttendanceFilter: filters.attendance,
            payloadPickupFilter: filters.pickup,
            formik,
            classRef,
            user,
            service,
            hasPermission,
            handleReset,
            handlePresent,
            handleUpdateAttendance,
            loadingUpdateAttendance,
            attendanceData,
            attendanceLoading,
            attendanceFetching,
            pickUpData,
            pickUpLoading,
            pickUpFetching,
            page,
            setPage,
            setShowAttendance,
            showAttendance
        }),
        [
            filters,
            formik,
            user,
            hasPermission,
            handleReset,
            handlePresent,
            handleUpdateAttendance,
            loadingUpdateAttendance,
            attendanceData,
            attendanceLoading,
            attendanceFetching,
            pickUpData,
            pickUpLoading,
            pickUpFetching,
            page,
            setPage,
            setShowAttendance,
            setShowPickup,
            showPickup
        ]
    );

    return <AttendanceContext.Provider value={contextValue}>{children}</AttendanceContext.Provider>;
};

AttendanceProvider.propTypes = {
    children: PropTypes.node
};

export default AttendanceContext;
